import createClient, { ClientOptions } from 'openapi-fetch';
import { ApiError } from './ApiError';
import type { paths } from './openapi';
import { uuid } from '@razz-team/utils';

export type ApiClient = ReturnType<typeof createClient<paths>>;

export interface ApiClientOptions extends ClientOptions {
  baseUrl: string;
  getSessionId(): string;
  refreshToken(): Promise<boolean>;
  getAccessToken(): string | null;
}

const unauthenticatedPaths = [
  '/auth/notOnlyActive',
  '/auth/token',
  '/otp/resend',
  '/threeDS/shortCode',
];

export function createApiClient({
  getSessionId,
  refreshToken,
  getAccessToken,
  ...options
}: ApiClientOptions): ApiClient {
  const apiFetch: typeof fetch = async (input, init) => {
    const request = new Request(input, init);
    const clone = request.clone();

    request.signal?.addEventListener('abort', () => {
      console.log(`[API] ${request.method} ${request.url} aborted`);
    });

    const response = await fetch(request);

    if (response.status === 401) {
      const url = new URL(request.url);
      if (url.pathname !== '/auth/token') {
        await refreshToken();
        const accessToken = getAccessToken();
        if (accessToken) {
          clone.headers.set('Authorization', `Bearer ${accessToken}`);
          return fetch(clone);
        }
      }
    }

    return response;
  };

  const client = createClient<paths>({
    ...options,
    // mode: 'cors',
    fetch: apiFetch,
  });

  client.use({
    async onRequest({ request, schemaPath }) {
      const sessionId = getSessionId();
      const accessToken = getAccessToken();

      if (import.meta.env.DEV) {
        if (request.method === 'POST' && !request.headers.has('Idempotency-Key')) {
          const error = new Error('Idempotency-Key is set for POST request');
          error.name = 'IdempotencyKeyRequired';
          throw error;
        }

        if (!accessToken && !unauthenticatedPaths.some((path) => schemaPath.startsWith(path))) {
          throw new Error(`accessToken is required for ${schemaPath}`);
        }
      }

      request.headers.set('X-Request-Id', uuid());
      request.headers.set('Session-Id', sessionId);
      if (accessToken) {
        request.headers.set('Authorization', `Bearer ${accessToken}`);
      }

      return request;
    },
    onResponse({ response, schemaPath }) {
      if (response.ok || schemaPath === '/auth/token') {
        return response;
      }

      return response
        .json()
        .catch(() => null)
        .then((json) => {
          throw new ApiError(
            json?.description || response.statusText,
            response.status,
            json?.name,
            json?.params,
          );
        });
    },
  });

  return client;
}
